import React, { useEffect, useState } from 'react';

/** url → object-URL still, or '' if capture failed. */
const frozenFrameCache = new Map();
/** url → in-flight capture promise */
const freezeInflight = new Map();

export function isLikelyAnimatedImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const base = url.split('#')[0].split('?')[0].toLowerCase();
  if (/\.gif$/i.test(base) || /^data:image\/gif/i.test(url)) return true;
  if (/\.(?:jpe?g|png|avif)$/i.test(base)) return false;
  /* KLIPY/Giphy/Tenor often serve animated webp as the "gif" / vibe_photo URL. */
  if (/(?:klipy\.com|giphy\.com|tenor\.com|media\.tenor)/i.test(url)) return true;
  return false;
}

export function spiritOverlaySourcesFromAd(ad, fallbackSrc) {
  const sg = ad?.surveyAnswers?.spirit_gif && typeof ad.surveyAnswers.spirit_gif === 'object'
    ? ad.surveyAnswers.spirit_gif
    : null;
  const animated =
    (typeof ad?.heartPhotoUrl === 'string' && ad.heartPhotoUrl)
    || (typeof ad?.surveyAnswers?.vibe_photo === 'string' && ad.surveyAnswers.vibe_photo)
    || (typeof sg?.gifUrl === 'string' && sg.gifUrl)
    || (typeof sg?.fullUrl === 'string' && sg.fullUrl)
    || fallbackSrc
    || null;
  /* Survey spirit picks are GIFs even when persisted as storage .webp (extension isn't enough). */
  const isSpiritPick = Boolean(sg && (sg.gifUrl || sg.fullUrl || sg.stillUrl || sg.id));
  const pauseOffCenter = isSpiritPick || isLikelyAnimatedImageUrl(animated);
  return { animatedSrc: animated, pauseOffCenter };
}

function isSameOriginUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('/')) return true;
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

function freezeGifFirstFrame(url) {
  const cached = frozenFrameCache.get(url);
  if (cached !== undefined) return Promise.resolve(cached === '' ? null : cached);
  const pending = freezeInflight.get(url);
  if (pending) return pending;

  const job = new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      img.onload = null;
      img.onerror = null;
      img.src = '';
      frozenFrameCache.set(url, value || '');
      resolve(value || null);
    };
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          finish(null);
          return;
        }
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (!blob) {
            finish(null);
            return;
          }
          finish(URL.createObjectURL(blob));
        }, 'image/jpeg', 0.72);
      } catch {
        finish(null);
      }
    };
    img.onerror = () => finish(null);
    img.decoding = 'async';
    if (!isSameOriginUrl(url)) img.crossOrigin = 'anonymous';
    img.src = url;
  }).finally(() => {
    freezeInflight.delete(url);
  });

  freezeInflight.set(url, job);
  return job;
}

/**
 * Spirit GIF overlay. Pass `animate={false}` to show a captured first frame so off-center
 * carousel cards don't keep decoding GIFs.
 */
export default function PartnerCutoutSpiritOverlay({
  src,
  animate = true,
  pauseOffCenter = false,
  captureStill = true,
  className = 'partner-cutout-gif-overlay',
  alt = '',
  loading,
  decoding = 'async',
  ...rest
}) {
  const cached = src ? frozenFrameCache.get(src) : '';
  const [frozenSrc, setFrozenSrc] = useState(() => (cached && cached !== '' ? cached : null));

  useEffect(() => {
    if (!src || !pauseOffCenter) {
      setFrozenSrc(null);
      return undefined;
    }
    const cachedStill = frozenFrameCache.get(src);
    if (cachedStill && cachedStill !== '') {
      setFrozenSrc(cachedStill);
      return undefined;
    }
    if (!captureStill) {
      setFrozenSrc(null);
      return undefined;
    }
    let cancelled = false;
    freezeGifFirstFrame(src).then((url) => {
      if (!cancelled) setFrozenSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [src, pauseOffCenter, captureStill]);

  if (!src) return null;

  const displaySrc = pauseOffCenter && !animate ? frozenSrc : src;
  if (!displaySrc) return null;

  return (
    <img
      {...rest}
      src={displaySrc}
      alt={alt}
      className={className}
      loading={loading ?? (animate ? 'eager' : 'lazy')}
      decoding={decoding}
    />
  );
}
